<script setup lang="ts">
import { onMounted, useRoute, watch } from '#imports'

export interface SaferGamblingEntrancePanelProps {
    title: string
    header?: string
    category: string
    backLabel: string
    /** False while the entrance shows another view. */
    active?: boolean
}

const props = withDefaults(defineProps<SaferGamblingEntrancePanelProps>(), {
    active: true
})

const emit = defineEmits<{
    goBack: []
    /** The route hash is the anchor of one of this panel's questions. */
    containsHash: []
}>()

const route = useRoute()

const { qas, isLoading } = await useSaferGamblingQas({
    key: `saferGambling:entranceQas:${props.category}`,
    category: props.category,
})

function emitIfHashInPanel() {
    if (qas.value.some(qa => qa.isQuestion && route.hash === `#${qa.id}`)) emit('containsHash')
}

onMounted(emitIfHashInPanel)
// The Q&As arrive after mount on a client-side navigation.
watch([() => route.hash, qas], emitIfHashInPanel)
</script>

<template>
    <div class="entrance-panel">
        <div class="entrance-panel__header">
            <span v-if="header">{{ header }}</span>
        </div>

        <div class="entrance-panel__content">
            <SaferGamblingQaPanel
                v-for="qa in qas"
                :id="qa.id"
                :key="qa.id"
                :is-question="qa.isQuestion"
                :active="props.active"
                :title="qa.title"
                :video-id="qa.video_id"
                :video-src="qa.video_src"
                :video-poster="qa.video_poster"
                :body="qa.body"
                :ctas="qa.ctas"
                :expandable-text-blocks="qa.expandableTextBlocks"
                borderless
            />

            <div v-if="isLoading" class="entrance-panel__empty">
                <UILoader />
            </div>
        </div>

        <div
            class="entrance-panel__return-link"
            @click="emit('goBack')"
        >
            <UIIcon class="entrance-panel__return-link-icon" name="arrow-left" size="8px" />
            <span class="entrance-panel__return-link-text">{{ props.backLabel }}</span>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.entrance-panel {
    padding: 15px;
    background-color: var(--panel-bg, $panel-color);
    border-radius: var(--safer-gambling-panel-border-radius, $safer-gambling-panel-border-radius);
    display: flex;
    flex-direction: column;
    gap: 8px;
    justify-content: center;

    &__return-link {
        background: none;
        border: none;
        font-weight: $text-bold;
        color: var(--on-link, $link-color);
        display: inline-flex;
        align-items: center;
        justify-content: center;

        &-text:hover {
            cursor: pointer;
            text-decoration: underline;
        }

        &-icon {
            margin-right: 8px;
        }
    }

    &__content {
        display: flex;
        flex-direction: column;
        gap: 15px;
        justify-content: center;
        align-items: center;
    }

    &__empty {
        display: flex;
        justify-content: center;
        padding: 20px;
    }

    :deep(.qa-panel) {
        width: 100%;

        .qa-panel__header {
            text-align: center;
            border-radius: var(--safer-gambling-panel-border-radius, $safer-gambling-panel-border-radius);
            background-color: var(--safer-gambling-qa-bg, $safer-gambling-qa-bg-color);
            justify-content: center;
            padding: 15px;
        }

        .qa-panel__header-icon {
            display: none;
            visibility: hidden;
        }
    }
}
</style>
